import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { CHARGE_PAYMENT_METHOD_OPTIONS } from "./ChargeCollectionOptions";

const TYPE_OPTIONS = [
  { value: "damage", label: "Damage / Repair" },
  { value: "cleaning", label: "Cleaning Fee" },
  { value: "late_fee", label: "Late Fee" },
  { value: "missing", label: "Missing Item Fee" },
  { value: "other", label: "Other Charge" },
];

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

function LegacyOriginal({ charge }) {
  const o = charge?.legacy_source?.original;
  if (!o) return null;
  return (
    <div className="bg-[#FAF8F0] border border-[#9A7B4F]/20 p-3 text-[11px] text-[#676664]" data-testid="legacy-original-block">
      <div className="text-[9px] uppercase tracking-wider text-[#9A7B4F] mb-1">Original record (kept unchanged)</div>
      <div>Late fee: ${Number(o.late_fee || 0).toFixed(2)} · Charged by Stripe: {o.late_fee_charged ? "yes" : "no"}{o.late_fee_waived ? " · Waived" : ""}</div>
      {o.actual_return_date && <div>Returned: {o.actual_return_date} (due {o.return_by_date || "—"})</div>}
    </div>
  );
}

export function EditChargeDialog({ charge, api, headers, onClose, onSaved }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const legacy = !!charge?.is_legacy_migrated;

  useEffect(() => {
    if (!charge) return;
    setDraft({
      charge_type: charge.charge_type,
      amount: String(charge.amount ?? ""),
      description: charge.description || "",
      status: charge.status,
      payment_method: charge.payment_method || "",
      payment_date: charge.payment_date || "",
    });
  }, [charge]);

  if (!charge || !draft) return null;
  const methodOptions = [...CHARGE_PAYMENT_METHOD_OPTIONS, { value: "bond_deduction", label: "Deducted from security bond" }];
  if (legacy || charge.payment_method === "stripe") methodOptions.push({ value: "stripe", label: "Stripe (card)" });
  const statusOptions = [{ value: "paid", label: "Paid" }, { value: "awaiting_external_payment", label: "Outstanding (collect later)" }];
  if (legacy) statusOptions.push({ value: "needs_review", label: "Needs review" });
  const isBond = draft.payment_method === "bond_deduction";

  const save = async () => {
    const amt = parseFloat(draft.amount);
    if (Number.isNaN(amt) || amt <= 0) { toast.error("Enter an amount greater than 0"); return; }
    const body = {};
    if (amt !== Number(charge.amount)) body.amount = amt;
    if (draft.description !== (charge.description || "")) body.description = draft.description;
    if (draft.charge_type !== charge.charge_type) body.charge_type = draft.charge_type;
    if (!isBond && draft.status !== charge.status) body.status = draft.status;
    if (draft.payment_method !== (charge.payment_method || "")) {
      if (isBond) body.deducted_from_bond = true;
      else {
        if (charge.payment_method === "bond_deduction") body.deducted_from_bond = false;
        body.payment_method = draft.payment_method;
      }
    }
    if (draft.payment_date !== (charge.payment_date || "")) body.payment_date = draft.payment_date;
    if (!Object.keys(body).length) { toast.message("No changes to save"); return; }
    setSaving(true);
    try {
      await axios.patch(`${api}/admin/bookings/charges/${charge.id}`, body, headers);
      toast.success("Charge updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update charge");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!charge} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-charge-dialog">
        <DialogHeader><DialogTitle className="text-xl text-[#676664]">{legacy ? "Correct historical charge" : "Edit charge"}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <LegacyOriginal charge={charge} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Charge type">
              <Select value={draft.charge_type} onValueChange={(v) => setDraft({ ...draft, charge_type: v })}>
                <SelectTrigger className="border-[#282828]/10" data-testid="edit-charge-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Amount ($)">
              <Input type="number" step="0.01" min="0" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} className="border-[#282828]/10" data-testid="edit-charge-amount-input" />
            </Field>
          </div>
          <Field label="Reason (customer-visible)">
            <Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="border-[#282828]/10" data-testid="edit-charge-description-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="How it was paid">
              <Select value={draft.payment_method || "none"} onValueChange={(v) => setDraft({ ...draft, payment_method: v === "none" ? "" : v, status: v === "bond_deduction" ? "paid" : (v === "none" && draft.status === "paid" ? "awaiting_external_payment" : draft.status) })}>
                <SelectTrigger className="border-[#282828]/10" data-testid="edit-charge-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not paid yet</SelectItem>
                  {methodOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={isBond ? "paid" : draft.status} disabled={isBond} onValueChange={(v) => setDraft({ ...draft, status: v })}>
                <SelectTrigger className="border-[#282828]/10" data-testid="edit-charge-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{statusOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Payment date">
            <Input type="date" value={draft.payment_date} onChange={(e) => setDraft({ ...draft, payment_date: e.target.value })} className="border-[#282828]/10" data-testid="edit-charge-date-input" />
          </Field>
          {isBond && <p className="text-[11px] text-[#676664]/70">If the bond is still held, this lowers the refundable bond. If it was already refunded, this only corrects the record.</p>}
          {(charge.edit_history || []).length > 0 && (
            <p className="text-[10px] text-[#676664]/50" data-testid="edit-charge-history-count">Edited {charge.edit_history.length} time(s) · every change is in the activity log.</p>
          )}
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="flex-1 border-[#282828]/10" data-testid="edit-charge-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="flex-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" data-testid="edit-charge-save">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Save changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
